import type { InquiryPublicDto } from '@paladin-monorepo/common';
import { InquiryStatus } from '@paladin-monorepo/common';
import { isDeadlineUrgent } from '@/utils/isDeadlineUrgent';
import { daysUntilInquiryOfferDeadline } from '@/utils/inquiryOfferDeadline';

export type DeadlinePriority = 'high' | 'medium' | 'low';

/** Project code when set, otherwise inquiry id (for dashboard labels). */
export function inquiryDisplayCode(inquiry: InquiryPublicDto): string {
  const code = inquiry.projectCode?.trim();
  return code || inquiry.id;
}

function isActiveInquiry(inquiry: InquiryPublicDto): boolean {
  return (
    inquiry.status !== InquiryStatus.Evaluated && inquiry.status !== InquiryStatus.Archived
  );
}

/**
 * Priority for next-action styling: overdue and within 7 days → high;
 * 8–14 days → medium; else low.
 */
export function priorityFromDeadline(deadline: string): DeadlinePriority {
  const days = daysUntilInquiryOfferDeadline(deadline);
  if (days < 0 || isDeadlineUrgent(deadline)) {
    return 'high';
  }
  if (days <= 14) {
    return 'medium';
  }
  return 'low';
}

/** Treats missing or non-finite budget parts as unspecified → 0 (avoids NaN in chart sums). */
function finiteBudgetPart(value: number | undefined | null): number | null {
  if (value == null) return null;
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
}

/**
 * Budget estimate for charts: midpoint of min/max when both set, else max, min, or 0.
 * Unspecified or invalid values count as 0 contribution so cumulative sums never become NaN.
 */
export function inquiryBudgetEstimate(inquiry: InquiryPublicDto): number {
  const min = finiteBudgetPart(inquiry.budgetMin);
  const max = finiteBudgetPart(inquiry.budgetMax);
  if (min != null && max != null) {
    return (min + max) / 2;
  }
  if (max != null) return max;
  if (min != null) return min;
  return 0;
}

/** Up to `limit` active inquiries, soonest deadline first. */
export function getNextActionInquiries(
  inquiries: InquiryPublicDto[],
  limit = 5
): InquiryPublicDto[] {
  const active = inquiries.filter(isActiveInquiry);
  active.sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
  return active.slice(0, limit);
}

/** Up to `limit` active inquiries for the projects table (recent activity first). */
export function getActiveProjectsForTable(
  inquiries: InquiryPublicDto[],
  limit = 10
): InquiryPublicDto[] {
  const active = inquiries.filter(isActiveInquiry);
  active.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  return active.slice(0, limit);
}

export interface PipelineMonthRow {
  /** YYYY-MM, end of that month included in cumulatives */
  monthKey: string;
  cumulativeInquiries: number;
  /** Sum of budget estimates for inquiries created on or before month end (same currency assumed). */
  cumulativeBudget: number;
}

const MONTHS_DEFAULT = 12;

/** Rolling window of month-ends; cumulative inquiries and cumulative budget up to each month end. */
export function buildMonthlyPipeline(
  inquiries: InquiryPublicDto[],
  months: number = MONTHS_DEFAULT
): PipelineMonthRow[] {
  const now = new Date();
  const rows: PipelineMonthRow[] = [];

  for (let i = months - 1; i >= 0; i--) {
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const endOfMonth = new Date(
      firstOfMonth.getFullYear(),
      firstOfMonth.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );
    const monthKey = `${firstOfMonth.getFullYear()}-${String(firstOfMonth.getMonth() + 1).padStart(2, '0')}`;

    let cumulativeInquiries = 0;
    let cumulativeBudget = 0;
    for (const inq of inquiries) {
      const created = new Date(inq.createdAt);
      if (created <= endOfMonth) {
        cumulativeInquiries += 1;
        cumulativeBudget += inquiryBudgetEstimate(inq);
      }
    }
    rows.push({ monthKey, cumulativeInquiries, cumulativeBudget });
  }

  return rows;
}

export interface StatusSegment {
  status: InquiryStatus;
  count: number;
}

/** Count per inquiry status (all enum values). */
export function countByStatus(inquiries: InquiryPublicDto[]): StatusSegment[] {
  const order: InquiryStatus[] = [
    InquiryStatus.Draft,
    InquiryStatus.InReview,
    InquiryStatus.Published,
    InquiryStatus.Evaluated,
    InquiryStatus.Archived,
  ];
  const map = new Map<InquiryStatus, number>();
  for (const s of order) {
    map.set(s, 0);
  }
  for (const inq of inquiries) {
    map.set(inq.status, (map.get(inq.status) ?? 0) + 1);
  }
  return order.map((status) => ({ status, count: map.get(status) ?? 0 }));
}

export interface CategoryCountRow {
  label: string;
  count: number;
}

/** Inquiries per category (by display name when present). */
export function countByCategory(inquiries: InquiryPublicDto[]): CategoryCountRow[] {
  const map = new Map<string, number>();
  for (const inq of inquiries) {
    const label = inq.category?.name?.trim() || inq.categoryId;
    map.set(label, (map.get(label) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

/** Most recently updated published inquiry (for matching candidates). */
export function getFeaturedPublishedInquiryId(inquiries: InquiryPublicDto[]): string | undefined {
  const published = inquiries.filter((i) => i.status === InquiryStatus.Published);
  if (published.length === 0) return undefined;
  published.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  return published[0]?.id;
}
